import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import CreateColumn from '../../../../Services/agGrid/createColumn';
import { Button } from '../../../../Components/MUIs';
import Commons from '../../../../Services/Common/Common';

/** R1 | 구분 중간결재자 ID / 경로 (TMS_ID 순) */
function parseTraCustom02MidSlots(row) {
  const idsRaw = row?.midSignIdsJoined ?? row?.MidSignIdsJoined ?? '';
  const pathsRaw = row?.midSignPathsJoined ?? row?.MidSignPathsJoined ?? '';
  const ids = String(idsRaw)
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean);
  const paths = String(pathsRaw).split('|');
  return ids.map((id, i) => ({
    id,
    path: String(paths[i] ?? '').trim(),
  }));
}

/**
 * done: 결재완료 뱃지
 * request: 신청인 → 결재요청 (최종 결재 완료 후에도 재상신 가능)
 * sign: 중간/최종 결재 대기 → 결재하기
 * none: 표시 없음
 */
function traCustom02ApprovalCellMode(row, userId) {
  const uid = String(Commons.DecryptoAES(userId) ?? '').trim();
  if (!row || !uid) return 'none';

  const createId = String(row.createId ?? row.CreateId ?? '').trim();
  if (createId && createId === uid) return 'request';

  const finalYn = String(
    row.finalSignYn ?? row.FinalSignYn ?? 'N'
  ).toUpperCase();
  if (finalYn === 'Y') return 'done';

  const slots = parseTraCustom02MidSlots(row);
  const mineMid = slots.find((s) => s.id === uid);
  if (mineMid && !mineMid.path) return 'sign';

  const finalId = String(row.finalSignId ?? row.FinalSignId ?? '').trim();
  const midYn = String(row.midSignYn ?? row.MidSignYn ?? 'N').toUpperCase();
  if (finalId && finalId === uid && (slots.length === 0 || midYn === 'Y')) {
    return 'sign';
  }

  return 'none';
}

/** 신청인(CREATE_ID) 여부 — 수정 vs 보기 버튼 분기 */
function isTraCustom02Applicant(row, userId) {
  const uid = String(Commons.DecryptoAES(userId) ?? '').trim();
  const createId = String(row?.createId ?? row?.CreateId ?? '').trim();
  return !!(createId && uid && createId === uid);
}

export const getColumnDefs1 = (props) => {
  return [
    CreateColumn('no', 'NO', 'right', 100, false, false, false, 'checkbox', {
      flex: 1,
      checkboxProps: {
        deleteNewRow: props.deleteNewRow1,
        restoreRow: props.restoreRow1,
        gridRef: props.gridRef,
        rowData: props.rowData1,
        selectedRowNosRef: props.selectedRowNosRef,
        lastCheckedIndexRef: props.lastCheckedIndexRef,
        handleRowSelection: props.handleRowSelection,
      },
      maxWidth: 100,
    }),

    CreateColumn(
      'custCd',
      '사업장코드',
      'left',
      90,
      false,
      false,
      true,
      'text',
      { flex: 1 }
    ),

    CreateColumn('eduDt', '교육일', 'center', 120, false, false, false, 'date'),

    CreateColumn(
      'selfYn',
      '등록구분',
      'center',
      118,
      false,
      false,
      false,
      'text',
      {
        maxWidth: 130,
        minWidth: 118,
        cellRenderer: (params) => {
          const yn = String(params.data?.selfYn ?? 'N').toUpperCase();
          const isSelf = yn === 'Y';
          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
              }}
            >
              <Chip
                label={isSelf ? '자체등록' : '세이프온'}
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: isSelf ? '#e8f5e9' : '#e3f2fd',
                  color: isSelf ? '#2e7d32' : '#1565c0',
                  border: '1px solid',
                  borderColor: isSelf ? '#a5d6a7' : '#90caf9',
                }}
              />
            </Box>
          );
        },
      }
    ),

    CreateColumn(
      'status',
      '상태',
      'center',
      110,
      false,
      false,
      false,
      'text',
      {
        maxWidth: 120,
        minWidth: 105,
        cellRenderer: (params) => {
          const v = String(
            params.data?.status ?? params.data?.Status ?? '대기'
          ).trim();
          const tone =
            v === '반려'
              ? { bgcolor: '#ffebee', color: '#c62828', border: '#ef9a9a' }
              : v === '결재완료'
                ? { bgcolor: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' }
                : v === '결재요청' || v === '승인요청'
                  ? {
                      bgcolor: '#fff8e1',
                      color: '#ef6c00',
                      border: '#ffe082',
                    }
                  : v === '결재대기'
                    ? {
                        bgcolor: '#eceff1',
                        color: '#546e7a',
                        border: '#cfd8dc',
                      }
                    : {
                        bgcolor: '#f5f5f5',
                        color: '#616161',
                        border: '#e0e0e0',
                      };
          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
              }}
            >
              <Chip
                label={v || '대기'}
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: tone.bgcolor,
                  color: tone.color,
                  border: '1px solid',
                  borderColor: tone.border,
                }}
              />
            </Box>
          );
        },
      }
    ),

    CreateColumn(
      'eduTm',
      '교육시간(시간)',
      'right',
      120,
      false,
      false,
      false,
      'number',
      {
        headerComponentParams: {
          template:
            '<div class="ag-header-cell-label"><span>교육시간<br>(시간)</span></div>',
        },
        customCellStyle: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          whiteSpace: 'pre-line',
          lineHeight: 1.2,
        },
        valueFormatter: (params) =>
          (params.value || '').toString().replace(/<br\s*\/?>/gi, '\n'),
      }
    ),

    CreateColumn(
      'eduPlace',
      '교육장소',
      'left',
      130,
      false,
      false,
      false,
      'text',
      {
        flex: 1,
        customCellStyle: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          whiteSpace: 'preLine',
          lineHeight: 1.2,
        },
      }
    ),

    CreateColumn(
      'eduTypeNm',
      '교육구분',
      'left',
      130,
      false,
      false,
      false,
      'text',
      {
        flex: 1,
        wrapText: true,
        autoHeight: true,
        customCellStyle: {
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          whiteSpace: 'preLine',
          lineHeight: 1.2,
        },
        valueFormatter: (params) =>
          (params.value || '').toString().replace(/<br\s*\/?>/gi, '\n'),
      }
    ),

    CreateColumn(
      'eduNmNm',
      '교육명',
      'left',
      300,
      false,
      false,
      false,
      'text',
      {
        flex: 1,
        minWidth: 260,
        maxWidth: 420,
        wrapText: true,
        autoHeight: true,
        customCellStyle: {
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          whiteSpace: 'preLine',
          lineHeight: 1.2,
        },
        valueFormatter: (params) =>
          (params.value || '').toString().replace(/<br\s*\/?>/gi, '\n'),
      }
    ),
    {
      headerName: '교육 강사',
      editable: true,
      headerClass: 'ag-center-header',
      children: [
        CreateColumn(
          'eduInstructorNm',
          '강사명',
          'left',
          120,
          false,
          false,
          false,
          'text',
          {
            flex: 1,
            minWidth: 100,
          }
        ),
        CreateColumn(
          'eduInstructorPosition',
          '직책',
          'left',
          110,
          false,
          false,
          false,
          'text',
          {
            flex: 1,
            minWidth: 90,
          }
        ),
        CreateColumn(
          'eduInstructorDepNm',
          '부서',
          'left',
          130,
          false,
          false,
          false,
          'text',
          {
            flex: 1,
            minWidth: 100,
          }
        ),
      ],
    },
    // CreateColumn(
    //   'eduContentsNm',
    //   '교육내용',
    //   'left',
    //   120,
    //   false,
    //   false,
    //   false,
    //   'text',
    //   {
    //     flex: 1,
    //     wrapText: true,
    //     autoHeight: true,
    //     customCellStyle: {
    //       display: 'flex',
    //       justifyContent: 'flex-start',
    //       alignItems: 'flex-start',
    //       whiteSpace: 'preLine',
    //       lineHeight: 1.2,
    //     },
    //     valueFormatter: (params) =>
    //       (params.value || '').toString().replace(/<br\s*\/?>/gi, '\n'),
    //   }
    // ),

    CreateColumn('', '상세', 'center', 100, false, false, false, 'text', {
      flex: 1,
      maxWidth: 100,
      customCellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      cellRenderer: (params) => {
        const data = params.data;
        if (!data || data.newYn === 'Y' || data.editYn === 'Y') {
          return null;
        }
        const isApplicant = isTraCustom02Applicant(
          data,
          props.userInfo?.userid ?? props.userInfo?.userId
        );
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <Button
              size="small"
              name={isApplicant ? '수정' : '보기'}
              sx={{ width: '100%', maxWidth: 96 }}
              onClick={() =>
                isApplicant
                  ? props.PopupClickEvent2Update?.(data)
                  : props.PopupClickEvent2View?.(data)
              }
            />
          </Box>
        );
      },
    }),
    CreateColumn('', '결재', 'center', 118, false, false, false, 'text', {
      flex: 1,
      maxWidth: 128,
      minWidth: 104,
      customCellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      cellRenderer: (params) => {
        const data = params.data;
        if (!data || data.newYn === 'Y' || data.editYn === 'Y') {
          return null;
        }
        const mode = traCustom02ApprovalCellMode(
          data,
          props.userInfo?.userid ?? props.userInfo?.userId
        );
        const wrap = (child) => (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            {child}
          </Box>
        );

        if (mode === 'done') {
          return wrap(
            <Chip
              label="결재완료"
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor: '#e8f5e9',
                color: '#2e7d32',
                border: '1px solid #a5d6a7',
              }}
            />
          );
        }
        if (mode === 'request') {
          const status = String(
            data?.status ?? data?.Status ?? '대기'
          ).trim();
          const canOpenReturnHistory = status === '반려';
          return wrap(
            <Box sx={{ display: 'flex', gap: 0.5, width: '100%', justifyContent: 'center' }}>
              <Button
                size="small"
                name="결재요청"
                sx={{ width: '100%', maxWidth: 96 }}
                onClick={() =>
                  props.PopupClickEvent2ApprovalRequest?.(params.data)
                }
              />
              <Button
                size="small"
                name="반려이력"
                variant="outlined"
                disabled={!canOpenReturnHistory}
                sx={{ width: '100%', maxWidth: 96 }}
                onClick={() =>
                  props.PopupClickEvent2ReturnHistory?.(params.data)
                }
              />
            </Box>
          );
        }
        if (mode === 'sign') {
          return wrap(
            <Button
              size="small"
              name="결재하기"
              sx={{ width: '100%', maxWidth: 96 }}
              onClick={() => props.PopupClickEvent2ApproverSign?.(params.data)}
            />
          );
        }
        return wrap(
          <Typography variant="caption" color="text.disabled">
            —
          </Typography>
        );
      },
    }),
  ];
};

const columnDefs = {
  getColumnDefs1,
};

export default columnDefs;
